import { useCallback, useMemo } from 'react';
import { UserFilter } from '../../../types/user.types';
import { Role } from '../../../types/role.types';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UseUserFiltersProps {
  filters: UserFilter;
  setFilters: React.Dispatch<React.SetStateAction<UserFilter>>;
  appliedFilters: UserFilter;
  onClearAppliedField: (patch: Partial<UserFilter>) => void;
  showMoreFilters: boolean;
  roles: Role[];
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
  onFilterChange?: (newFilters: Partial<UserFilter>) => void;
}

export const useUserFilters = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  roles,
  onSearch,
  onReset,
  onToggleMoreFilters,
  onFilterChange,
}: UseUserFiltersProps) => {
  const handleInputChange = useCallback(
    (e: any) => {
      const { name, value } = e.target as HTMLInputElement;
      setFilters((prev) => ({ ...prev, [name]: value }));
      onFilterChange?.({ [name]: value } as Partial<UserFilter>);
    },
    [setFilters, onFilterChange],
  );

  const handleSelectChange = useCallback(
    (e: any) => {
      const { name, value } = e.target as HTMLInputElement;
      setFilters((prev) => ({ ...prev, [name]: value }));
      onFilterChange?.({ [name]: value } as Partial<UserFilter>);
    },
    [setFilters, onFilterChange],
  );

  const handleRoleSelectChange = useCallback(
    (e: any) => {
      const value = (e.target as HTMLInputElement).value;
      const roleIds = Array.isArray(value) ? value : (value as string).split(',');
      setFilters((prev) => ({ ...prev, roleIds }));
      onFilterChange?.({ roleIds });
    },
    [setFilters, onFilterChange],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSearch();
      }
    },
    [onSearch],
  );

  const handleDateChange = useCallback(
    (name: 'startDate' | 'endDate', value: string) => {
      const parsedValue = value || undefined;
      setFilters((prev) => ({ ...prev, [name]: parsedValue }));
      onFilterChange?.({ [name]: parsedValue });
    },
    [setFilters, onFilterChange],
  );

  const renderStatusValue = useCallback((selected: unknown): React.ReactNode => {
    if (!selected || selected === 'all') {
      return <span style={{ color: 'gray' }}>Todos</span>;
    }
    if (selected === 'active') return 'Activo';
    if (selected === 'blocked') return 'Bloqueado';
    return String(selected);
  }, []);

  const renderSortByValue = useCallback((selected: unknown): React.ReactNode => {
    if (!selected) return <span style={{ color: 'gray' }}>Seleccionar</span>;
    if (selected === 'email') return 'Correo';
    if (selected === 'firstName') return 'Nombre';
    if (selected === 'lastName') return 'Apellido';
    if (selected === 'createdAt') return 'Fecha de creación';
    if (selected === 'updatedAt') return 'Última actualización';
    return String(selected);
  }, []);

  const renderRoleValue = useCallback(
    (selected: unknown): React.ReactNode => {
      const selectedRoles = Array.isArray(selected) ? selected : [];

      if (!selectedRoles.length) {
        return <span style={{ color: 'gray' }}>Seleccionar</span>;
      }

      const labels = selectedRoles.map((roleId) => {
        const role = roles.find((item) => item.id === roleId);
        return role?.name || roleId;
      });

      return labels.join(', ');
    },
    [roles],
  );

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.searchTerm) {
      chips.push({
        key: 'searchTerm',
        label: `Búsqueda: ${applied.searchTerm}`,
        onRemove: () => onClearAppliedField({ searchTerm: '' }),
      });
    }
    if (applied.status && applied.status !== 'all') {
      const statusLabel =
        applied.status === 'active'
          ? 'Activo'
          : applied.status === 'blocked'
            ? 'Bloqueado'
            : applied.status;
      chips.push({
        key: 'status',
        label: `Estado: ${statusLabel}`,
        onRemove: () => onClearAppliedField({ status: 'all' }),
      });
    }
    (applied.roleIds || []).forEach((roleId) => {
      const roleName = roles.find((r) => r.id === roleId)?.name || roleId;
      chips.push({
        key: `role:${roleId}`,
        label: `Rol: ${roleName}`,
        onRemove: () =>
          onClearAppliedField({
            roleIds: (applied.roleIds || []).filter((id) => id !== roleId),
          }),
      });
    });
    if (applied.startDate) {
      chips.push({
        key: 'startDate',
        label: `Desde: ${applied.startDate}`,
        onRemove: () => onClearAppliedField({ startDate: undefined }),
      });
    }
    if (applied.endDate) {
      chips.push({
        key: 'endDate',
        label: `Hasta: ${applied.endDate}`,
        onRemove: () => onClearAppliedField({ endDate: undefined }),
      });
    }

    return chips;
  }, [appliedFilters, roles, onClearAppliedField]);

  return {
    filters,
    showMoreFilters,
    handleInputChange,
    handleSelectChange,
    handleRoleSelectChange,
    handleKeyPress,
    handleDateChange,
    renderStatusValue,
    renderSortByValue,
    renderRoleValue,
    onSearch,
    onReset,
    onToggleMoreFilters,
    activeFilterChips,
  };
};
